import { Link } from 'react-router-dom';
import { Github, Twitter, Linkedin, Youtube, Mail, MapPin, Code2 } from 'lucide-react';
import { Logo } from '@/components/ui/Logo';
import { FlagIcon } from '@/components/ui/FlagIcon';
import { useTranslation } from 'react-i18next';

interface FooterLink {
  nameKey: string;
  href: string;
  external?: boolean;
}

interface FooterSection {
  titleKey: string;
  links: FooterLink[];
}

const footerLinksConfig: Record<string, FooterSection> = {
  products: {
    titleKey: 'footer.products',
    links: [
      { nameKey: 'Ochtopus IDE', href: 'https://ochtopus.com', external: true },
      { nameKey: 'Minder', href: '/products#minder' },
      { nameKey: 'Fulfillment', href: '/products#fulfillment' },
      { nameKey: 'footer.links.ecommerceIntegrator', href: '/products#entegrator' },
      { nameKey: 'CyberShield', href: '/products#cybershield' },
    ],
  },
  services: {
    titleKey: 'footer.services',
    links: [
      { nameKey: 'footer.links.customSoftware', href: '/services#custom-software' },
      { nameKey: 'footer.links.webDevelopment', href: '/services#web-development' },
      { nameKey: 'footer.links.mobileApp', href: '/services#mobile-development' },
      { nameKey: 'footer.links.cybersecurity', href: '/services#cybersecurity' },
      { nameKey: 'footer.links.aiConsulting', href: '/services#ai-consulting' },
    ],
  },
  company: {
    titleKey: 'footer.company',
    links: [
      { nameKey: 'footer.about', href: '/about' },
      { nameKey: 'footer.careers', href: '/careers' },
      { nameKey: 'footer.blog', href: '/blog' },
      { nameKey: 'footer.contact', href: '/contact' },
    ],
  },
  legal: {
    titleKey: 'footer.legal',
    links: [
      { nameKey: 'footer.privacy', href: '/privacy' },
      { nameKey: 'footer.terms', href: '/terms' },
      { nameKey: 'footer.kvkk', href: '/privacy#kvkk' },
      { nameKey: 'footer.cookies', href: '/cookies' },
      { nameKey: 'footer.security', href: '/security' },
    ],
  },
};

const socialLinks = [
  { name: 'GitHub', href: 'https://github.com/bosphoruslabs', icon: Github },
  { name: 'Twitter', href: 'https://twitter.com/bosphoruslabs', icon: Twitter },
  { name: 'LinkedIn', href: 'https://linkedin.com/company/bosphoruslabs', icon: Linkedin },
  { name: 'YouTube', href: 'https://youtube.com/@bosphoruslabs', icon: Youtube },
];

export function Footer() {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  // Helper function to get translated name or use key as-is for product names
  const getLinkName = (nameKey: string) => {
    // Product names that shouldn't be translated
    const productNames = ['Ochtopus IDE', 'Minder', 'Fulfillment', 'CyberShield'];
    if (productNames.includes(nameKey)) {
      return nameKey;
    }
    return t(nameKey);
  };

  return (
    <footer className="relative bg-bg-surface border-t border-border-default pt-12 mt-12">
      {/* Main Footer Content */}
      <div className="container pt-16 pb-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Brand Column - Takes 2 cols on lg */}
          <div className="col-span-2">
            <Link to="/" className="inline-block mb-6">
              <Logo size={36} />
            </Link>
            <p className="text-text-secondary text-sm mb-6 max-w-sm leading-relaxed">
              {t('footer.tagline')}
            </p>

            {/* Contact Info */}
            <div className="space-y-3 mb-6">
              <a href="mailto:info@bosphoruslabs.com" className="flex items-center gap-3 text-text-secondary hover:text-accent transition-colors text-sm group">
                <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                  <Mail size={14} className="text-accent" />
                </div>
                info@bosphoruslabs.com
              </a>
              <div className="flex items-center gap-3 text-text-secondary text-sm">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <MapPin size={14} className="text-blue-400" />
                </div>
                {isEnglish ? 'Istanbul, Turkey' : 'İstanbul, Türkiye'}
              </div>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-2 mb-8">
              {socialLinks.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl bg-bg-card border border-border-default flex items-center justify-center text-text-secondary hover:text-accent hover:border-accent/50 hover:bg-accent/5 transition-all"
                  aria-label={item.name}
                >
                  <item.icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Link Columns */}
          {Object.entries(footerLinksConfig).map(([key, section]) => (
            <div key={key}>
              <h3 className="font-semibold text-text-primary mb-4">{t(section.titleKey)}</h3>
              <ul className="space-y-2.5">
                {section.links.map((link) => (
                  <li key={link.nameKey}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-accent transition-colors group"
                      >
                        {getLinkName(link.nameKey)}
                      </a>
                    ) : link.href.startsWith('/') ? (
                      <Link to={link.href} className="text-sm text-text-secondary hover:text-accent transition-colors">
                        {getLinkName(link.nameKey)}
                      </Link>
                    ) : (
                      <a href={link.href} className="text-sm text-text-secondary hover:text-accent transition-colors">
                        {getLinkName(link.nameKey)}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-border-default mt-8 py-5">
        <div className="container flex items-center justify-center min-h-[40px]">
          <div className="w-full flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Copyright */}
            <p className="text-text-tertiary text-sm">
              &copy; {new Date().getFullYear()} Bosphorus Labs. {t('footer.copyright')}
            </p>

            {/* Made in Turkey Badge */}
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-2 text-text-tertiary text-sm">
                <Code2 size={16} className="text-accent flex-shrink-0" />
                <span>{t('footer.developedBy')}</span>
              </div>
              <div className="flex items-center gap-2">
                <FlagIcon code="tr" size={20} />
                <span className="text-sm text-text-tertiary">{isEnglish ? 'Istanbul' : 'İstanbul'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
